import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import {
  DropDown,
  Textarea,
} from 'anf-core-react';

import ExchangeButton from '../exchange-button/exchange-button';
import TmntText from '../tmnt-text/tmnt-text';

export default function ProductExchangeSurveyReasonCodes({
  product,
  options,
  onChange,
  selectedProductComments,
  canExchange = true,
}) {
  const [dreTextAreaCount, ChangeDreTextAreaCount] = useState(0);
  const exchangeSecondaryReasonLabel = TmntText({ k: 'DRE_WHAT_WAS_THE_PROBLEM', textOnly: true });
  const exchangeReasonLabel = TmntText({ k: 'DRE_WHY_ARE_YOU_RETURNING', textOnly: true });
  const exchangeCommentsLabel = TmntText({ k: 'DRE_ADDITIONAL_COMMENTS', textOnly: true });

  const exchangeReason = useMemo(() => {
    const comments = selectedProductComments[product.orderLineKey];

    return (
      <DropDown
        id={`product-exchange-survey-reason-${product.primeLineKey}`}
        label={exchangeReasonLabel}
        value={comments?.exchangeReason?.value || ''}
        isRequired
        options={options}
        onChange={
          (e) => onChange(product.orderLineKey, {
            ...comments,
            exchangeReason: options.find((o) => o.value === e.target.value),
            exchangeSecondaryReason: null,
          })
        }
      />
    );
  }, [selectedProductComments, product, onChange, exchangeReasonLabel]);

  const exchangeSecondaryReason = useMemo(() => {
    const comments = selectedProductComments[product.orderLineKey];
    if (comments?.exchangeReason?.type !== 'options') return null;

    return (
      <div className="w-100">
        <DropDown
          id={`product-exchange-survey-reason-${product.orderLineKey}-secondary`}
          label={exchangeSecondaryReasonLabel}
          value={comments?.exchangeSecondaryReason?.value || ''}
          options={comments?.exchangeReason?.options}
          onChange={
            (e) => onChange(
              product.orderLineKey,
              {
                ...comments,
                exchangeSecondaryReason: comments
                  .exchangeReason.options.find((o) => o.value === e.target.value),
              },
            )
          }
        />
      </div>
    );
  }, [selectedProductComments, product, onChange, exchangeSecondaryReasonLabel]);

  const dreTextareaMaxLength = 120;

  const additionalComments = useMemo(() => {
    const comments = selectedProductComments[product.orderLineKey];
    if (comments?.exchangeReason?.type !== 'textbox' && comments?.exchangeSecondaryReason?.type !== 'textbox') {
      return null;
    }

    if (comments?.exchangeComment) {
      ChangeDreTextAreaCount(comments.exchangeComment.length);
    }

    return (
      <span data-property="DRE_ADDITIONAL_COMMENTS">
        <Textarea
          id={`product-exchange-survey-comment-${product.primeLineKey}`}
          label={exchangeCommentsLabel}
          maxLength={dreTextareaMaxLength}
          value={comments?.exchangeComment}
          onChange={
          (e) => onChange(product.orderLineKey, {
            ...comments,
            exchangeComment: e.target.value,
          }, ChangeDreTextAreaCount(e.target.value.length))
        }
        />
        <span className="textarea dre-textarea">
          <small data-property="DRE_TEXTBOX_CHARCOUNT" role="alert" aria-live="polite" aria-atomic="true" className="maxlength" id={`product-exchange-survey-comment-${product.orderLineKey}-char-count`}>
            <TmntText k="DRE_TEXTBOX_CHARCOUNT" textOnly />
            <span className="char-count">
              {dreTextareaMaxLength - dreTextAreaCount}
            </span>
          </small>
        </span>
      </span>
    );
  }, [selectedProductComments, product, onChange, dreTextAreaCount, exchangeCommentsLabel]);

  return (
    <div className="product-exchange-survey-wrapper" data-testid="product-exchange-survey-wrapper">

      {exchangeReason}
      {exchangeSecondaryReason}
      {additionalComments}

      {canExchange && product.name && (
        <ExchangeButton product={product} k="DRE_EXCHANGE_FOR_NEW_SIZE_COLOR" />
      )}
    </div>
  );
}

ProductExchangeSurveyReasonCodes.propTypes = {
  product: PropTypes.shape({
    imageId: PropTypes.string,
    productPricePaidFmt: PropTypes.string,
    partNumber: PropTypes.string,
    name: PropTypes.string,
    productId: PropTypes.string,
    orderLineKey: PropTypes.string.isRequired,
    collection: PropTypes.string,
    exchange: PropTypes.shape({
      size: PropTypes.string,
      color: PropTypes.string,
    }),
    primeLineKey: PropTypes.string,
  }),
  selectedProductComments: PropTypes.shape({
    exchangeReason: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.string,
          type: PropTypes.string,
        }),
      ),
    }),
    exchangeSecondaryReason: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      type: PropTypes.string,
    }),
    exchangeComment: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  canExchange: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      value: PropTypes.string,
      type: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.string,
          type: PropTypes.string,
          value: PropTypes.string,
        }),
      ),
    }),
  ),
};
